import { useEffect, useState } from "react";

import { Button, Dialog, DialogClose, Field, Money, Notice, Textarea } from "../../../../components/ui";
import { actionError, periodFacts, periodName } from "./payrollFinance";

const REMARKS_MAX = 500;
const REASON_MAX = 500;
const meaningful = (text) => String(text || "").replace(/\s+/g, " ").trim();

// The three period decisions Finance owns. The server decides whether each is
// allowed; the copy says what the decision does and what happens next.
const MODES = {
  approve: {
    title: "Approve payroll for release?",
    explain: "HR can then release the payslips to employees. Salaries are paid after release, under To pay.",
    label: "Notes for HR",
    hint: "Optional.",
    required: false,
    confirm: "Approve for release",
    done: (name) => `${name} approved. HR can now release the payslips.`,
  },
  // APP-HR-101: the owner approving payroll they prepared, because nobody else can. Never worded as a review.
  approve_exception: {
    title: "Approve payroll you prepared, as the owner?",
    explain: "Nobody else in this business can approve payroll, so you may approve your own work. It is recorded as an owner exception, not an independent review, and your reason stays in this payroll's review history.",
    label: "Notes for HR",
    hint: "Optional.",
    required: false,
    confirm: "Approve as owner exception",
    done: (name) => `${name} approved as an owner exception. HR can now release the payslips.`,
  },
  return: {
    title: "Return payroll to HR?",
    explain: "HR corrects it and sends it back for your decision. Nothing is paid.",
    label: "What should HR correct?",
    hint: null,
    required: true,
    confirm: "Return to HR",
    done: (name) => `${name} returned to HR for correction.`,
  },
  settle: {
    title: "Mark this payroll settled?",
    explain: "Every employee has confirmed receiving their salary. Settling closes the period; it pays nobody.",
    label: "Remarks",
    hint: "Optional.",
    required: false,
    confirm: "Mark settled",
    done: (name) => `${name} is settled.`,
  },
};

/**
 * PeriodDecisionDialog — approve, return or settle one payroll period.
 * `onSubmit(remarks)` performs the request; a rejected promise keeps the
 * dialog open with the server's plain message.
 */
export default function PeriodDecisionDialog({ mode, period, prepared = "", reasonMinLength = 10, onClose, onSubmit }) {
  const config = MODES[mode];
  const exception = mode === "approve_exception";
  const [remarks, setRemarks] = useState("");
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [failure, setFailure] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRemarks("");
    setReason("");
    setReasonError("");
    setFieldError("");
    setFailure("");
    setBusy(false);
  }, [mode, period?.id]);

  if (!config || !period) return null;
  const name = periodName(period);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    const value = remarks.trim();
    if (config.required && !value) {
      setFieldError("Tell HR what to correct.");
      return;
    }
    const exceptionReason = meaningful(reason);
    if (exception && exceptionReason.length < reasonMinLength) {
      setReasonError(`Give a reason of at least ${reasonMinLength} characters.`);
      return;
    }
    setBusy(true);
    setFailure("");
    try {
      await onSubmit(value || undefined, config.done(name), exception ? { exceptionReason } : undefined);
    } catch (error) {
      setFailure(actionError(error, "This decision couldn't be saved. Try again."));
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => { if (!open && !busy) onClose(); }}
      title={config.title}
      description={config.explain}
      footer={
        <>
          <DialogClose asChild>
            <Button disabled={busy}>Cancel</Button>
          </DialogClose>
          <Button variant="primary" type="submit" form="period-decision-form" loading={busy}>
            {config.confirm}
          </Button>
        </>
      }
    >
      <form id="period-decision-form" onSubmit={submit} noValidate className="space-y-4">
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 rounded-panel bg-surface-sunken px-4 py-3 text-body-sm">
          <dt className="text-secondary">Payroll</dt>
          <dd className="font-medium text-foreground">{name}</dd>
          <dt className="text-secondary">Covers</dt>
          <dd className="text-foreground">{periodFacts(period)}</dd>
          <dt className="text-secondary">Net pay</dt>
          <dd><Money value={period.net_pay_cents} className="text-money text-foreground" /></dd>
          {prepared && mode !== "settle" ? (
            <>
              <dt className="text-secondary">Prepared</dt>
              <dd className="text-foreground">{prepared}</dd>
            </>
          ) : null}
        </dl>
        {failure ? <Notice tone="danger" role="alert">{failure}</Notice> : null}
        {exception ? (
          <Field label="Why you are approving your own payroll" hint={`At least ${reasonMinLength} characters. Kept in the review history.`} error={reasonError} required>
            <Textarea
              value={reason}
              maxLength={REASON_MAX}
              onChange={(event) => {
                setReason(event.target.value);
                if (reasonError) setReasonError("");
              }}
            />
          </Field>
        ) : null}
        <Field label={config.label} hint={config.hint} error={fieldError} required={config.required}>
          <Textarea
            value={remarks}
            maxLength={REMARKS_MAX}
            onChange={(event) => {
              setRemarks(event.target.value);
              if (fieldError) setFieldError("");
            }}
          />
        </Field>
      </form>
    </Dialog>
  );
}
